import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  QueryList,
  ViewChildren
} from '@angular/core';
import { MessageToolbarComponent } from '../../message-toolbar/message-toolbar.component';
import { SbMessageComponent } from '../../sb-message/sb-message.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { SbInformationsAnzeigeComponent } from '../../sb-informations-anzeige/sb-informations-anzeige.component';
import { DisplayItem, DisplayItemType } from '../../sb-model/display-item';
import { DisplayMessage } from '../../sb-model/display-message';
import { DisplayMessageFile } from '../../sb-model/display-message-file';
import { DisplayInfo } from '../../sb-model/display-info';
import { MatrixService } from '../../../../services/matrix/matrix.service';
import { ChatMessageService } from '../../../../services/chat-message/chat-message.service';
import { DisplayMessageFileAdapter } from '../../sb-model/display-message-file-adapter';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { RoomHelperService } from '../../../../services/room/room-helper.service';
import { debounceTime, distinctUntilChanged, merge, Subject, take } from 'rxjs';
import { ReceiptService } from '../../../../services/receiptService/receipt.service';
import { ChatStoreService } from '../../../../services/chat-store/chat-store.service';
import { RoomListAccessService } from '../../../../services/room-list/room-list-access.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbMessageStreamTestIds } from './sb-message-stream.test-ids';

@Component({
  selector: 'app-message-stream',
  imports: [MessageToolbarComponent, SbMessageComponent, NgClass, SbInformationsAnzeigeComponent, AsyncPipe],
  templateUrl: './sb-message-stream.component.html',
  styleUrl: './sb-message-stream.component.scss'
})
/**
 * Nachrichten-Stream im mittleren Bereich.
 * Begründung: Im Zuge des Architektur-Refactorings entstanden (keine unmittelbare AFO)
 */
export class SbMessageStreamComponent implements AfterViewInit, OnDestroy {
  readonly chatContainer = input.required<HTMLElement>();
  readonly readMarkerId = input<string>();
  @ViewChildren('messageDiv')
  messageElementRef!: QueryList<ElementRef>;
  readonly testIds = SbMessageStreamTestIds;
  readonly matrix = inject(MatrixService);
  readonly chatMessageService = inject(ChatMessageService);
  readonly roomHelperService = inject(RoomHelperService);
  protected readonly DisplayItemType = DisplayItemType;
  protected toolbarMessageId = '';
  protected readonly KnownMembership = KnownMembership;
  protected readonly roomListAccessService = inject(RoomListAccessService);
  private readonly receiptService = inject(ReceiptService);
  private readonly chatStoreService = inject(ChatStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private visibleMap = new Map<Element, number>();
  private readonly lastVisibleMsgId$ = new Subject<string>();
  private observer!: IntersectionObserver;

  ngOnDestroy(): void {
    this.observer.disconnect();
  }

  ngAfterViewInit(): void {
    merge(
      this.messageElementRef.changes.pipe(take(1)),
      this.chatStoreService.getCurrentRoomId$().pipe(takeUntilDestroyed(this.destroyRef))
    ).subscribe(() =>
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          this.scrollToFullyRead();
        });
      })
    );

    this.setupObserver();

    this.lastVisibleMsgId$
      .asObservable()
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(id => {
        (async (): Promise<void> => {
          const roomId = this.chatStoreService.getStateSync().currentRoomId;
          if (roomId) {
            await this.receiptService.markAsFullyRead(roomId, id);
          }
        })();
      });
  }

  setToolbarMessageId($event: string): void {
    this.toolbarMessageId = $event;
  }

  asDisplayMessage: (di: DisplayItem) => DisplayMessage | undefined = (di: DisplayItem) =>
    di instanceof DisplayMessage ? di : undefined;

  // TODO Workaround => ausbauen!
  asDisplayMessageFile: (di: DisplayItem) => DisplayMessageFile | undefined = (di: DisplayItem) =>
    di instanceof DisplayMessageFile ? di : this.displayMessageToDisplayMessageFile(this.asDisplayMessage(di));

  asDisplayInfo: (di: DisplayItem) => DisplayInfo | undefined = (di: DisplayItem) =>
    di instanceof DisplayInfo ? di : undefined;

  private scrollToFullyRead(): void {
    const readMarkerId = this.readMarkerId();
    if (!readMarkerId) {
      return;
    }

    const markerEl = document.getElementById('lastRead_' + readMarkerId);
    const container = this.chatContainer();
    if (!container || !markerEl) {
      return;
    }
    const scrollTop = markerEl.offsetTop - 20 - container.offsetTop;
    container.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }

  /**
   * Hier wird ein Observer vom Typ IntersectObserver aufgesetzt, welcher den Viewport des ChatContainer observed und uns
   * dann sagen kann, ob eine Nachricht zu 50% im Viewport zu sehen ist. Anhand dieser Information wird dann mit der relativen
   * Position im Container errechnet, welches das unterste Element ist und daraus wird dann die Id genommen.
   * @private
   */
  private setupObserver(): void {
    const options = {
      root: this.chatContainer(),
      threshold: 0.5
    };

    this.observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const element = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          const top = element.offsetTop;
          this.visibleMap.set(entry.target, top);
        } else {
          this.visibleMap.delete(entry.target);
        }
      }
      this.emitLastVisibleId();
    }, options);

    this.messageElementRef.changes.subscribe(() => this.updateElements());
    this.updateElements();
  }

  /**
   * Die ID des letzten sichtbaren Elements wird emitted
   * @private
   */
  private emitLastVisibleId(): void {
    if (this.visibleMap.size === 0) return;

    const lastVisible = [...this.visibleMap.entries()].sort((a, b) => b[1] - a[1])[0][0];

    const lastId = lastVisible.querySelector('[id]')?.id;
    if (lastId) {
      this.lastVisibleMsgId$.next(lastId);
    }
  }

  /**
   * Kalkuliere die Map neu, wenn ein Element in die Liste kommt, welches zuvor nicht da war.
   * @private
   */
  private recalcVisiblePositions(): void {
    const containerRect = this.chatContainer().getBoundingClientRect();
    const newMap = new Map<Element, number>();

    this.visibleMap.forEach((_, el) => {
      const rect = el.getBoundingClientRect();

      const stillVisible = rect.bottom >= containerRect.top && rect.top <= containerRect.bottom;

      if (stillVisible) {
        newMap.set(el, rect.top);
      }
    });

    this.visibleMap = newMap;
  }

  /**
   * Wenn sich die Elemente verändern soll der observer auf die neuen elemente schauen.
   * @private
   */
  private updateElements(): void {
    setTimeout(() => {
      this.messageElementRef.forEach(el => {
        this.observer.observe(el.nativeElement);
      });

      this.recalcVisiblePositions();
      this.emitLastVisibleId();
    });
  }

  private readonly displayMessageToDisplayMessageFile: (dm?: DisplayMessage) => DisplayMessageFile | undefined = (
    dm?: DisplayMessage
  ) => (dm ? DisplayMessageFileAdapter.messageToMessageFile(dm) : undefined);
}
